/**
 * Phase 6e2c-0 — COMPONENT SELECTOR CHASSIS (§30).
 *
 * React port of component_selector.js MainView (538) + component_selector.tmpl
 * (156), BOARD host only (`header:false`). Mounts inside #component_rating_mode
 * (BoardContent §30.1) and is the consumer of `rating_mode:activate` /
 * `rating_mode:hide` / `rating_mode:mounting:changed` that the form/tab emitters
 * already fire (no consumer existed before this build).
 *
 * Contract coverage:
 *  §30.2 activation + unsaved-changes guard (#save_changes_popup, makeChanges).
 *  §30.3 tab strip + lazy per-selector construction (selectorRegistry; ONE
 *        active tab + content div + .control-buttons in board mode).
 *  §30.4 dynamic button fan-out `<render>_<modelId>:selector:<action>` +
 *        chassis-level `cancel:dialog:changes` on hide.
 *  §30.5 rotateCanvas / hideRatingDialogBox CSS state machine
 *        (.main-rotation .rotate / .rating-mode-active / .loading, #ahu_shy,
 *        inline width, 200 ms leave delay, force+hasChanges guard,
 *        rating_mode:hide:<id> after Select via the selector).
 *  §30.6 number-input suite — REUSED from formKit (BareInput); not rebuilt.
 *  §30.7 notes popup — shared NotesPopup (chassis-owned).
 *
 * The legacy view does jQuery DOM surgery on .main-rotation; the existing React
 * board (CompTable) already toggles those same classes imperatively, and the
 * canvas reads `.main-rotation.rotate` — so this chassis stays compatible by
 * doing the same imperative class toggling rather than React-managing the card.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  openRatingRequest,
  setPanelCollapsed,
  setRatingDialogOpen,
} from 'app/slices/ahuBoardSlice';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { ROTATE_CARD_MS } from '../../components/CompTable';
import { getSelectorEntry } from './selectorRegistry';
import { NotesPopup } from './NotesPopup';
import { ParkedVendorPlaceholder } from './ParkedVendorPlaceholder';
import { useRatingNumberKeypress } from './useRatingNumberKeypress';
import type { RatingModeActivate, SelectorAction } from './selectorTypes';

interface ComponentSelectorDialogProps {
  ahu: { id: number } & Record<string, unknown>;
}

/** The chassis's current open state (null = closed / no dialog rendered). */
interface DialogState {
  /** active selector tab name (= show_tab). */
  name: string;
  /** model_id for the §30.4 fan-out (current_model.id, AHU id for AHU-level). */
  modelId: number;
  activate: RatingModeActivate;
  /** Remount key — bumped on every FULL open. Legacy destroys + re-creates the
   * selector view per activation (component_selector.js renderFromAHU 91 →
   * renderComponentRatingMode 143), then re-derives the buttons from the new
   * view's rated state (checkButtonsAvailability, 171/180). Keying the body on
   * this seq gives React the same semantics: a full open ALWAYS mounts a fresh
   * selector whose mount-time arming effect re-derives #select — even when the
   * previous dialog was the same component type (React would otherwise keep
   * the instance and never re-run the arming effect). */
  openSeq: number;
}

const mainRotation = () => document.querySelector<HTMLElement>('.main-rotation');

export const ComponentSelectorDialog = ({ ahu }: ComponentSelectorDialogProps) => {
  const dispatch = useAppDispatch();
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const [loadingOnly, setLoadingOnly] = useState(false);
  const [selectEnabled, setSelectEnabled] = useState(false);
  // `#print` availability. Legacy renders the button `non-clickable`
  // (component_selector.tmpl:61) and the SELECTOR removes the class from its own
  // rated state — `self.$el.parents('.ahu-rate-content').find('#print')
  // .removeClass('non-clickable')` (coil.selector.js:1626-1629 / :2269-2272,
  // fan.selector.js:3357-3364 / :4002-4009). That DOM reach is this setter, and
  // the board chassis simply never passed it, so a rated coil/fan left #print
  // permanently greyed here while dev enables it. Same wiring the header host
  // already has (IndependentRatingPopup.tsx:157).
  const [printEnabled, setPrintEnabled] = useState(false);
  const [savePopupOpen, setSavePopupOpen] = useState(false);
  const [notes, setNotes] = useState<Array<{ note: string }> | null>(null);

  // §30.6 addendum — the chassis's `keypress input[data-type="number"], …`
  // character filter (component_selector.js:33), delegated like legacy's.
  useRatingNumberKeypress();

  // Synchronous mirror of `dialog` so renderFromAHU (re-entered from a
  // same-tick duplicate `rating_mode:activate` emit) sees the up-to-date open
  // state without widening its dep list (dep churn on the bus effect risked
  // the earlier chassis loop regressions). Always write through setDialogSync.
  const dialogRef = useRef<DialogState | null>(null);
  /**
   * Bumped on every write to the dialog body. hideRatingDialogBox defers its
   * unmount by one card turn (see there), and anything that puts a NEW body up
   * in the meantime has to invalidate that pending teardown or it would stomp
   * the dialog that replaced it.
   */
  const bodySeq = useRef(0);
  /** A close is riding out its card turn: logically shut, still painted. */
  const closing = useRef(false);
  const setDialogSync = useCallback((d: DialogState | null) => {
    bodySeq.current += 1;
    dialogRef.current = d;
    setDialog(d);
  }, []);
  /** Full-open counter feeding DialogState.openSeq (remount key). */
  const openSeq = useRef(0);
  /** Latest renderFromAHU, for the deferred phase-2 re-entry (see below). */
  const renderFromAHURef = useRef<
    ((options: RatingModeActivate, afterSmartListClose?: boolean) => void) | null
  >(null);

  // hasChanges tracker (§30.2 makeChanges): set on a change while #select is
  // clickable. Kept in a ref so the activate handler reads it synchronously.
  const hasChanges = useRef(false);
  const tableRotateStatus = useRef(false);
  // Pending activate held while the save-changes popup is open.
  const pendingActivate = useRef<RatingModeActivate | null>(null);

  // Synchronous mirror of `selectEnabled` so makeChanges (re-entered from the
  // selector's same-tick `mounting:changed` emit) reads the up-to-date #select
  // gate instead of the stale render-closure state. The selector is handed the
  // wrapped setter below.
  const selectEnabledRef = useRef(false);
  const setSelectEnabledSync = useCallback((enabled: boolean) => {
    selectEnabledRef.current = enabled;
    setSelectEnabled(enabled);
  }, []);

  // ---- §30.5 rotateCanvas ----
  const rotateCanvas = useCallback(() => {
    const el = mainRotation();
    if (!el) return;
    // A close may still be riding out its turn — hideRatingDialogBox holds
    // `rating-mode-active` for the animation. This open supersedes it, so drop
    // the leftover class before the toggle below reads it: otherwise the branch
    // reads "already active" and schedules a removal 200 ms into the NEW
    // dialog's life, blanking it.
    if (closing.current) {
      el.classList.remove('rating-mode-active');
      closing.current = false;
    }
    if (el.classList.contains('loading')) {
      el.classList.remove('rating-mode-active');
    } else {
      el.classList.toggle('rotate');
    }
    if (el.classList.contains('rating-mode-active')) {
      window.setTimeout(() => el.classList.remove('rating-mode-active'), 200);
    } else {
      el.classList.add('rating-mode-active');
      // ★ #ahu_shy must be driven through REDUX, not setAttribute.
      // Legacy is `$('#ahu_shy').attr("checked","checked")`
      // (component_selector.js:208) on a plain uncontrolled checkbox
      // (main.tmpl:17), where the content attribute still governs
      // checkedness — so `:checked` flips and the CSS-only collapse fires:
      //   #ahu_shy:checked ~ #components_section { width: 49px }  (styles.css:911)
      //   #ahu_shy:checked ~ .main-rotation .rating-mode-wrapper {
      //     width: calc(100% - 57px) }                            (styles.css:932)
      // i.e. the strip gives the dialog back 309-49 = 260px.
      // React's #ahu_shy is CONTROLLED (BoardContent.tsx `checked={panelCollapsed}`),
      // so React assigns the `checked` PROPERTY at mount, which sets the input's
      // dirty-checkedness flag — from then on setAttribute('checked') no longer
      // moves `:checked`. The literal port was therefore a silent no-op: the
      // strip never collapsed and the dialog rendered ~260px narrower than
      // legacy, overflowing `.rating-mode-wrapper { overflow-x:auto }`
      // (rating_mode.scss:2344) into a horizontal scrollbar.
      // Dispatching is also the parity-correct trigger: legacy's attr() write
      // fires no click event, so `changeMainRotationWidth` (main.js:218, bound
      // on `click #ahu_shy`) does NOT run on dialog open — and neither does
      // React's onAhuShyToggle, which is an onChange handler and only fires on
      // real user interaction. The 475px inline width set in renderFromAHU
      // stays authoritative, exactly as in legacy.
      dispatch(setPanelCollapsed(true));
    }
    tableRotateStatus.current = el.classList.contains('rotate');
  }, [dispatch]);

  /**
   * Retire the pending `rating.requestedSectionId` once a dialog is actually
   * on screen. hideRatingDialogBox has always done this on CLOSE, for the
   * reason spelled out there: a component form that REMOUNTS (the COMPONENT →
   * ACCESSORIES → COMPONENT roundtrip) resets its `lastRatingToken` ref to 0
   * and re-consumes whatever request is still pending, re-firing its opener.
   *
   * That replay used to be harmless — it emitted a duplicate
   * `rating_mode:activate` for the already-open selector, which renderFromAHU
   * swallows as a no-op. Now that a repeat open is a CLOSE (the toggle in
   * useComponentForm.openRatingStub), the same replay would shut the dialog by
   * itself on a tab roundtrip. So the request has to be retired on OPEN too:
   * it has been served, and nothing may consume it twice.
   *
   * Only reachable once a form has emitted its activate, so a form that mounts
   * AFTER the dispatch (the psychrometric-jump / canvas paths) still finds the
   * request waiting for it.
   */
  const markRequestServed = useCallback(() => {
    dispatch(openRatingRequest(null));
  }, [dispatch]);

  // ---- §30.5 hideRatingDialogBox ----
  const hideRatingDialogBox = useCallback(
    (force?: boolean) => {
      if (force && hasChanges.current) return; // unsaved-guard
      // Click the filter's embedded cancel (DOM parity — no React filter yet).
      document
        .querySelector<HTMLButtonElement>(
          '#component_rating_mode .content-with-pagers.filter button[name=cancel_btn]'
        )
        ?.click();
      const el = mainRotation();
      // Start the turn. Everything that makes the dialog VISIBLE is held for the
      // length of it, below — legacy drops `rating-mode-active` after 200 ms
      // (component_selector.js:224-227), a fifth of the 0.73 s turn, and React
      // additionally unmounted the body on the first frame, so the back face was
      // already empty by the time it came into view. Same defect the smart list
      // had on its way out, same shape of fix.
      el?.classList.remove('rotate');
      // Width goes back NOW, like legacy (`$('.main-rotation').css({width:''})`,
      // component_selector.js:230) — it is layout, not content, and relaxing it
      // mid-turn would resize the card while it is moving.
      el?.removeAttribute('style');
      // `hide-shadow` is added on OPEN by the accessory selector
      // (AccessorySelector.tsx, legacy accessory.selector.js:78 — evap_pack:166
      // and filter:62 do the same) and it paints a 50x50 #f2efed patch over the
      // board's top-right corner (`.main-rotation.hide-shadow::before`,
      // rating_mode.scss:769). Legacy drops it here: the navy panel listens on
      // `rating_mode:hide` and does `$('.main-rotation').removeClass
      // ('hide-shadow')` (tabs.navy.panel.js:63-67). Nothing in React removed
      // it, so the patch survived every close and stayed on the board for the
      // rest of the session. Removing it in hideRatingDialogBox covers EVERY
      // close path — the bus event, #cancel, the save-changes popup and the
      // deleted-accessory auto-close all funnel through here.
      el?.classList.remove('hide-shadow');
      // Mirror of the rotateCanvas write above — legacy's
      // `$('#ahu_shy').removeAttr("checked")` (component_selector.js:229) was
      // dead in React for the same controlled-input reason.
      dispatch(setPanelCollapsed(false));
      tableRotateStatus.current = false;
      document
        .querySelectorAll('.ahu-header-btns .active-mode.rating')
        .forEach((b) => b.classList.remove('non-clickable'));
      ahuBoardBus.emit('cancel:dialog:changes');
      hasChanges.current = false;
      // Drop any pending rating request so a component-form REMOUNT (tab
      // roundtrip: COMPONENT → ACCESSORIES → COMPONENT) cannot replay a stale
      // token and spontaneously re-emit `rating_mode:activate`
      // (useComponentForm's lastRatingToken ref resets to 0 on remount — the
      // 07-08 regression (a) trigger). Legacy has no such replay: navy
      // showRating clicks a live label each time (tabs.navy.panel.js).
      dispatch(openRatingRequest(null));
      // §13 rows 6/7 — the dialog is closed: drop `#show_rating.active` and the
      // per-component open gate (legacy tabs.navy.panel.js:63-67 on
      // `rating_mode:hide`, SelectionMode.deactivateRatingMode:219-225).
      dispatch(setRatingDialogOpen(null));
      // Tear down the React dialog. The LOGICAL half is immediate — everything
      // that decides what a subsequent open does reads `dialogRef` / these refs,
      // and they must say "closed" the moment the close starts (legacy clears
      // `tableRotateStatus` and `ratingModeActive` here for the same reason).
      selectEnabledRef.current = false;
      dialogRef.current = null;
      setSelectEnabled(false);
      setPrintEnabled(false);
      // The VISIBLE half rides out the turn, so the dialog is still painted on
      // the face while the face is still pointing at the user. Guarded by
      // bodySeq: a new dialog opened during the turn owns the body now.
      bodySeq.current += 1;
      closing.current = true;
      const seq = bodySeq.current;
      window.setTimeout(() => {
        if (seq !== bodySeq.current) return;
        closing.current = false;
        el?.classList.remove('rating-mode-active');
        setDialog(null);
        setLoadingOnly(false);
        setSavePopupOpen(false);
        setNotes(null);
      }, ROTATE_CARD_MS);
    },
    [dispatch]
  );

  // ---- §30.2 renderFromAHU (the activation consumer) ----
  const renderFromAHU = useCallback(
    (options: RatingModeActivate, afterSmartListClose = false) => {
      const el = mainRotation();

      // ★ PHASE 1 — LET THE SMART LIST TURN AWAY FIRST.
      //
      // The smart list (CompTable) and this dialog are the SAME flipped face of
      // `.main-rotation`, and one `.rotate-card` transition turns it
      // (styles.css:8800 — 0.73s linear). `.rotate` says which way the card is
      // facing; `manual-mode-active` / `rating-mode-active` say which content is
      // painted on the back.
      //
      // Legacy swaps them in a single statement — `$('.main-rotation
      // .manual-mode-active').attr('class', 'main-rotation')` then straight into
      // rotateCanvas (tabs.navy.panel.js showRating:145-146, and the same two
      // lines again on the RATING radio, selection_mode.js:92-93). The card is
      // mid-flip in both states, so it never turns: the list is simply replaced
      // by the dialog between two frames.
      //
      // QA asked for the two movements to be separate and to read as one
      // transition, so the swap is sequenced here instead: hand the close to
      // CompTable (`rotate:canvas` — its own rotateBack, the same close the
      // list's CLOSE button runs), wait out the turn, then re-enter and let
      // rotateCanvas play the open. `manual-mode-active` is the list's marker
      // and it is held for exactly that turn, so its presence is the test for
      // "the list is on the face right now".
      if (!afterSmartListClose && el?.classList.contains('manual-mode-active')) {
        ahuBoardBus.emit('rotate:canvas');
        window.setTimeout(() => {
          // The close leaves `main-rotation` bare; belt-and-braces in case a
          // later timer of its own has not landed yet, so rotateCanvas below
          // always starts from a settled, canvas-side face.
          const face = mainRotation();
          if (face) face.className = 'main-rotation';
          renderFromAHURef.current?.(options, true);
        }, ROTATE_CARD_MS);
        return;
      }

      el?.style.setProperty('width', 'calc(100% - 475px)');

      // Resolved ahead of the preloader branch because BOTH branches put a
      // dialog on screen and so both have to publish the open gate below.
      const modelId =
        options.current_model && typeof options.current_model.id === 'number'
          ? (options.current_model.id as number)
          : ahu.id;

      // §30.2 loading:true — preloader-only one-shot — ONLY for selectors that
      // have no native React render yet (parked vendors handle their own body;
      // real React selectors self-load). The board's section-form rating stub
      // (useComponentForm.openRatingStub) emits loading:true; a parked vendor
      // tab is rendered directly so there is no stuck spinner. Real selectors
      // (special_options) are opened by TabAhu WITHOUT loading and fall through.
      const entry = getSelectorEntry(options.name);
      const willRenderBody = Boolean(entry?.render) || Boolean(entry?.def.parked);
      if (options.loading && !willRenderBody) {
        setLoadingOnly(true);
        setDialogSync(null);
        dispatch(setRatingDialogOpen({ name: options.name, modelId }));
        markRequestServed();
        rotateCanvas();
        el?.classList.add('loading');
        return;
      }
      el?.classList.remove('loading');

      // Unsaved-changes guard: already active + no preloader + hasChanges →
      // show the save-changes popup and stash the pending activate (§30.2).
      const isActive = el?.classList.contains('rating-mode-active');
      if (isActive && !loadingOnly && hasChanges.current) {
        pendingActivate.current = options;
        setSavePopupOpen(true);
        return;
      }

      // Re-activating the SAME already-open selector (same tab + same model,
      // e.g. a #show_rating re-click, or a duplicate activate replayed by a
      // remounting form) is a NO-OP for the dialog body. Legacy parity: on
      // every re-activation the Backbone chassis destroys + re-creates the
      // selector view and then RE-DERIVES #select from the selector's own
      // rated state (component_selector.js renderComponentRatingMode 143-184 →
      // e.g. fan.selector.js checkButtonsAvailability 3648: `choosen` set ⇒
      // remove non-clickable) — so an armed Select is NEVER killed by a
      // re-activate. React keeps the mounted selector instance here (same
      // element type + position), so its mount-time arming effect would NOT
      // re-run — falling through to the selectEnabled reset below permanently
      // disarmed #select (2026-07-08 verified regressions (a)/(b)). Keeping
      // the mounted selector untouched yields the same outcome as legacy's
      // re-derivation: a displayed rated model keeps Select armed.
      const open = dialogRef.current;
      if (open && open.name === options.name && open.modelId === modelId) {
        return;
      }

      // Uncheck any open accessory rating radios (left panel parity).
      document.querySelectorAll('.accessory > label.open').forEach((label) => {
        label.parentElement
          ?.querySelector<HTMLInputElement>('input[name="select_mode_id"]')
          ?.removeAttribute('checked');
        label.classList.remove('open');
      });

      setLoadingOnly(false);
      selectEnabledRef.current = false;
      setSelectEnabled(false);
      // A full open re-renders the template, so #print starts `non-clickable`
      // again; the freshly mounted selector (key=openSeq) re-derives it. The
      // same-selector re-activate above early-returns before this, so an armed
      // #print survives a re-click exactly like #select.
      setPrintEnabled(false);
      hasChanges.current = false;
      openSeq.current += 1;
      setDialogSync({
        name: options.name,
        modelId,
        activate: options,
        openSeq: openSeq.current,
      });
      // §13 rows 6/7 — publish `#show_rating.active` + the per-component open
      // gate. Legacy exempts the UVC selector from the navy `active` class
      // (tabs.navy.panel.js:68-73) — and from THAT only: UVC is an accessory
      // selector with no navy button of its own, but its
      // `SelectionMode.ratingModeActive` is set like every other component's,
      // which is what makes a second click on the accessory's own Rating label
      // a CLOSE. So suppress the navy half, not the whole dispatch.
      dispatch(
        setRatingDialogOpen({
          name: options.name,
          modelId,
          navy: options.name !== 'uvc',
        })
      );
      markRequestServed();

      // rotate only when not already rotated (or re-activating the same tab).
      if (!tableRotateStatus.current) rotateCanvas();
    },
    [ahu.id, dispatch, loadingOnly, markRequestServed, rotateCanvas, setDialogSync]
  );

  // Phase 2 of the smart-list handover re-enters renderFromAHU from a timer, by
  // which point the callback may have been re-created (its deps churn on every
  // open). The ref keeps that timer pointed at the CURRENT one.
  useEffect(() => {
    renderFromAHURef.current = renderFromAHU;
  }, [renderFromAHU]);

  // ---- §30.4 fan-out: chassis button → `<render>_<modelId>:selector:<action>` ----
  const fireSelectorAction = useCallback(
    (action: SelectorAction) => {
      if (!dialog) return;
      if (action === 'print') {
        const original = document.body.style.zoom;
        document.body.style.zoom = '';
        ahuBoardBus.emit(`${dialog.name}_${dialog.modelId}:selector:print`);
        window.setTimeout(() => {
          document.body.style.zoom = original;
        }, 300);
        return;
      }
      ahuBoardBus.emit(`${dialog.name}_${dialog.modelId}:selector:${action}`);
    },
    [dialog]
  );

  // ---- §30.2 makeChanges (any input/select change while #select clickable) ----
  // Legacy: `change input`/`change select` delegated on the chassis → hasChanges =
  // `!#select.non-clickable`. In the React port the selector emits
  // `rating_mode:mounting:changed` ONLY from its own dirty path (markDirty fires
  // it the same tick it calls setSelectEnabled(true)) — so it ALWAYS means a real
  // user change while #select just became clickable. Reading the `selectEnabled`
  // STATE here is a stale-closure trap (setSelectEnabled(true) has not flushed
  // when the synchronous emit re-enters this callback → it would latch false and
  // the unsaved-guard would never arm). Set the ref directly: the event itself is
  // the proof of a change. (`selectEnabledRef` mirrors legacy's #select gate for
  // the rare external emitter that fires before any selector enabled Select.)
  const makeChanges = useCallback(() => {
    hasChanges.current = selectEnabledRef.current;
  }, []);

  // …and the DOM half of the same binding, which the note above describes but the
  // port never wired. `rating_mode:mounting:changed` only covers the fields that
  // route through a selector's markChanged(); legacy's two delegated rows cover
  // EVERY input and select in the chassis, so anything the selector stages
  // without going through markChanged still armed the guard in dev.
  //
  // Measured gap (dev3, Opening dialog, AHU 6484): editing "Distance to Wall Ups"
  // (`position_y`) and then clicking SELECT on another Opening switched straight
  // over with no popup — the edit was gone. `tag`, `air_stream_id` and
  // `desired_face_velocity_actual` did arm it, which is why this reads as
  // intermittent rather than broken.
  //
  // Delegated on `#component_rating_mode` — the host wrapper (BoardContent:506),
  // i.e. the same element legacy's `self.$el` is — and on the NATIVE `change`
  // event, not React's onChange (which for text inputs is the `input` event and
  // would arm on every keystroke instead of on commit). Idempotent with the bus
  // emit: both just latch `hasChanges` to the current #select gate, and legacy
  // double-fires here too.
  useEffect(() => {
    const host = document.getElementById('component_rating_mode');
    if (!host) return;
    const onChange = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t instanceof HTMLInputElement || t instanceof HTMLSelectElement) makeChanges();
    };
    host.addEventListener('change', onChange);
    return () => host.removeEventListener('change', onChange);
  }, [makeChanges]);

  // ---- save-changes popup (§30.2) ----
  // ★ Legacy's saveChanges CLICKS the real button — `self.$el.find(btn).click()`
  // (component_selector.js:190, btn = the pressed popup button's `data-for`) — so
  // the popup path runs the WHOLE of #select / #cancel, not just the §30.4 fan-out
  // half. This port fired only the fan-out, which is right for #select (the
  // button's onClick is nothing else) but drops half of #cancel: the button also
  // calls hideRatingDialogBox(). Legacy gets there through the selector —
  // `#cancel` → `cancel` → `<name>_<id>:selector:cancel` WITH the click event →
  // accessory.selector.js cancelChanges(e):2948 `if (e) Dispatcher.trigger
  // ("rating_mode:hide")` — whereas the React selector's cancelChanges takes no
  // event and the chassis button calls hide itself (see #cancel's onClick below).
  //
  // Consequence of the miss, measured on dev3 vs dev (AHU 6484, two Openings in
  // one section): hideRatingDialogBox is what resets `tableRotateStatus` to false
  // and clears `hasChanges`. Skipping it left tableRotateStatus true, so the
  // re-open below hit `if (!tableRotateStatus.current) rotateCanvas()` and never
  // re-added `.rating-mode-active` — which the line above had just removed. dev
  // ends the switch on `main-rotation rotate rating-mode-active hide-shadow`;
  // dev3 ended on `main-rotation rotate hide-shadow`. That class is the first
  // term of renderFromAHU's unsaved-changes guard (`isActive`), so from then on
  // the guard was DEAD for the rest of the session: every later switch discarded
  // the user's edits with no popup at all. (The #select branch was unaffected —
  // AccessorySelector.selectAccessory emits `rating_mode:hide` itself when its
  // PATCHes land, which happened inside the 300 ms here.)
  const onSaveChanges = useCallback(
    (target: 'select' | 'cancel') => {
      setSavePopupOpen(false);
      fireSelectorAction(target);
      if (target === 'cancel') hideRatingDialogBox();
      window.setTimeout(() => {
        mainRotation()?.classList.remove('rating-mode-active');
        const next = pendingActivate.current;
        pendingActivate.current = null;
        if (next) {
          // Legacy saveChanges → renderFromAHU builds a FRESH view from
          // persisted state (component_selector.js 187-196). Close the dialog
          // ref first so the pending activate takes the FULL open path (a
          // pending re-open of the SAME selector must not hit the same-selector
          // no-op — it needs the remount to discard abandoned edits and
          // re-derive #select, exactly like legacy's re-render).
          setDialogSync(null);
          renderFromAHU(next);
        }
      }, 300);
    },
    [fireSelectorAction, hideRatingDialogBox, renderFromAHU, setDialogSync]
  );

  // ---- bus wiring (initialize): the three chassis subscriptions ----
  useEffect(() => {
    const offs = [
      ahuBoardBus.on('rating_mode:activate', (options) =>
        renderFromAHU(options as RatingModeActivate)
      ),
      ahuBoardBus.on('rating_mode:hide', (force) =>
        hideRatingDialogBox(Boolean(force))
      ),
      ahuBoardBus.on('rating_mode:mounting:changed', () => makeChanges()),
      // Every Delete Accessory flow (TabAccessories.tsx removeAccessory, the
      // fan VFD uncheck in fanVfdPersist.ts) emits this once the DELETE
      // succeeds. If THIS dialog is open for the accessory that just got
      // deleted, its underlying record is gone — close it automatically rather
      // than leaving the user editing/selecting a component for an accessory
      // that no longer exists.
      //
      // ★ This used to listen on `remove_accessory_from_canvas`, which is NOT a
      // deletion signal — the accessory form fires it from the draw gate's
      // remove paths too (useAccessoryForm → accessoryDrawGate), for a live row
      // that simply must not be drawn. A 4-sided hood with no `height_2` hits
      // `checkForHoodStyle4NoHeight2` on EVERY re-render, so the dialog closed
      // itself the instant it opened: clicking a collapsed row's Select made
      // AccessorySelector auto-expand the row (AccessorySelector.tsx:1782,
      // legacy initialize:67-103), the re-render re-ran the gate, the emit
      // landed here, and hideRatingDialogBox tore the dialog straight back down
      // — via `cancel:dialog:changes` → cancelChanges, which re-collapsed the
      // row as well, leaving no trace that anything had happened.
      //
      // No `force`: every real emit site passes none (force is reserved for a
      // nav-away guard that BLOCKS the hide when there are unsaved changes —
      // the opposite of what a deletion should do here).
      ahuBoardBus.on('accessory:deleted', (id) => {
        if (dialog?.modelId === id) hideRatingDialogBox();
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [renderFromAHU, hideRatingDialogBox, makeChanges, dialog]);

  // ---- render ----
  if (loadingOnly) {
    return <div className="ahu-rate-content content-with-pagers loading-host">
      <div className="preloader dotted-preloader active" />
    </div>;
  }

  if (!dialog) return null;

  const entry = getSelectorEntry(dialog.name);
  const def = entry?.def;
  const isAccessory = Boolean(def?.isAccessory);
  const isParked = Boolean(def?.parked) || !entry?.render;
  // ★ The legacy `show_tab` string. Every chassis hook below is built out of it
  // in dev (component_selector.js:133-146 / the tmpl's `ifCompare show_tab`
  // branches), so it — NOT the registry key — is what reaches the DOM and what
  // the `show_tab` gates compare against. They differ only for the wheel's two
  // alias registrations (er_wheel / rotary → energy_wheel); see
  // SelectorTabDef.showTab. The fan-out key stays on `dialog.name`: that one IS
  // the name the selector was opened under and subscribes to (§30.4).
  const showTab = def?.showTab ?? dialog.name;
  // #print/#reset/#rate visibility: hidden for AHU-level/accessory selectors.
  const showRate = !isAccessory && showTab !== 'special_options';
  // §30.3: energy_wheel / plate_heat_exchanger have NO print button
  // (.ahu-rate-content.hide-print). Suppress #print for those tabs.
  const hidePrint = ['energy_wheel', 'plate_heat_exchanger'].includes(showTab);
  // §30.3 — `#reset`. component_selector.tmpl:62-76 emits it behind FIVE
  // `ifCompare show_tab` branches and nothing else: fan, coil, gas_heater,
  // energy_wheel, plate_heat_exchanger. Every other tab (uvc, filter,
  // evap_pack, the four accessories, special_options) has no Reset button in
  // dev, so this list IS the legacy gate, not a subset of it.
  //
  // ★ `fan` was held back when PR #1099 landed the other four — that build was
  // scoped "every rating dialog except Fan". Reported missing since, so the
  // list is now dev's five verbatim. FanSelector's `doReset` was dead code for
  // exactly as long as it was excluded, and reading as an 8-key wipe rather
  // than dev's spread-and-override; it was rebuilt in the same change. Do not
  // re-enable one without the other.
  //
  // The header host does NOT get this button: it renders with show_tab:'all'
  // (IndependentRatingPopup.tsx:239), which matches none of the five branches.
  const showReset = [
    'fan',
    'coil',
    'gas_heater',
    'energy_wheel',
    'plate_heat_exchanger',
  ].includes(showTab);
  // Legacy renders `#reset` `non-clickable` and each selector clears the class
  // unconditionally from its own render/initialize — coil.selector.js:1652
  // (render), gas_heater.selector.js:1150, energy_wheel.selector.js:331 and
  // plate_heat_exchanger.selector.js:215 (both initialize). None of the four
  // gates it on rated state (contrast `#print`/`#select`), so the button is
  // live as soon as the selector body mounts. A parked vendor placeholder
  // mounts no selector and therefore has nothing subscribed to the fan-out —
  // it stays greyed rather than being a silent no-op.
  const resetEnabled = !isParked;

  // #select clickable when a real selector enabled it. A REAL accessory render
  // (6e2c-3 <AccessorySelector>) drives the gate itself via _isSelectValid →
  // setSelectEnabled (legacy _updateSelectButtonState toggles #select.
  // non-clickable on every staging change). Only a parked/no-render accessory
  // tab falls back to the legacy "starts enabled" shortcut.
  const hasRealRender = Boolean(entry?.render);
  const selectClickable = (isAccessory && !hasRealRender) || selectEnabled;

  // COMBO LOUVER (KA-5245) — A1.1 tab label. component_selector.tmpl:47:
  //   <a value="louver" …>{{#if is_combo}}Combo {{/if}}Louver</a>
  // `is_combo` comes off the activation's current_model
  // (component_selector.js:94) and is consumed ONLY inside the `louver` branch of
  // the tab strip, so the prefix is gated on the tab name here too.
  const isComboLouverTab =
    dialog.name === 'louver' &&
    Boolean((dialog.activate.current_model as Record<string, unknown> | null | undefined)?.is_combo);
  const tabLabel = `${isComboLouverTab ? 'Combo ' : ''}${def?.label ?? dialog.name}`;

  return (
    <div className={`ahu-rate-content content-with-pagers ${showTab}${hidePrint ? ' hide-print' : ''}`}>
      <ul className={`clearfix list-inline content-pager ${showTab}`}>
        <li className="active">
          <a value={showTab} name="rating_mode_button">
            {tabLabel}
          </a>
        </li>
        <li className="control-header-buttons">
          {showRate && (
            <button
              id="rate"
              className={`right btn-add ${showTab}-rate${
                def?.rateNonClickable ? ' non-clickable' : ''
              }`}
              tabIndex={-1}
              data-type={showTab}
              onClick={() => {
                if (def?.rateNonClickable) return; // §33.9 #rate permanently off
                fireSelectorAction('rate');
              }}
            >
              rate
            </button>
          )}
          {!isAccessory && showTab !== 'special_options' && !hidePrint && (
            <button
              id="print"
              className={`print-btn icon-print${printEnabled ? '' : ' non-clickable'}`}
              onClick={() => {
                if (!printEnabled) return; // `.non-clickable` blocks the real click too
                fireSelectorAction('print');
              }}
            />
          )}
          {/* component_selector.tmpl:63 verbatim (markup AND DOM order: #reset
              is emitted AFTER #print and floats itself with `left`). */}
          {showReset && (
            <button
              id="reset"
              className={`left icon-refresh-1 ka-has-tooltip${resetEnabled ? '' : ' non-clickable'}`}
              tooltip="Reset to defaults values"
              onClick={() => {
                if (!resetEnabled) return;
                fireSelectorAction('reset');
              }}
            />
          )}
        </li>
      </ul>

      <div className="ahu-rate-content-item ahu-rate-content-item-with-subtabs rate-items">
        {/* key=openSeq — every full open mounts a FRESH selector (legacy
            destroys + re-creates the view per activation and re-derives the
            #select gate from it; see DialogState.openSeq). */}
        {/* ★ OCCLUSION FIX (pre-existing, ALL accessory types — not combo-only).
            component_selector.tmpl:114-123 gives EACH of the four accessory
            content divs the class `rating_mode_accessory_content`:
              <div id="rating_mode_{opening|hood|damper|louver}_content"
                   class="rating_mode_accessory_content">
            React emitted `class=""`, so the whole height chain collapsed. That
            class is what carries `height: 100%` and `> div { height: 100% }`
            (rating_mode.scss:2160 / :2214) plus the accessory padding
            (`30px 30px 45px 30px !important`, :2214). Without it the dialog body
            is content-sized instead of filling the host, so
            `.accessory_selector_dialog .set-min-height`'s
            `min-height: calc(100% - 260px)` (:2389-2391) resolves against a zero
            height and the SECOND content band never clears the absolutely
            positioned `.mounting-flip-front` 3-D popup. Measured chain — legacy
            `997 → 922 → 904 → 644`, React `467 → 467 → 0 → 0`; `elementFromPoint`
            over `airflow_actual`, `mounting_plane_id`, all four distance rows and
            (once wave 1 added them) `enclosure_depth` / `enclosure_material_id`
            all returned `.mounting-flip-front`. Fixed by EMITTING LEGACY'S CLASS
            — deliberately no new CSS. `isAccessory` is set on exactly the four
            types the template gates on (selectorRegistry: opening / hood /
            damper / louver), so this is a 1:1 port of the template condition.
            NB `#rating_mode_uvc_content` / `#rating_mode_evap_pack_content` share
            those SCSS blocks by ID and are unaffected. */}
        <div
          id={`rating_mode_${showTab}_content`}
          className={isAccessory ? 'rating_mode_accessory_content' : undefined}
          key={dialog.openSeq}
        >
          {isParked ? (
            <ParkedVendorPlaceholder label={def?.label ?? dialog.name} />
          ) : (
            entry?.render?.({
              modelId: dialog.modelId,
              ahu,
              activate: dialog.activate,
              setSelectEnabled: setSelectEnabledSync,
              setPrintEnabled,
              showNotes: (n) => setNotes(n),
            })
          )}
        </div>
      </div>

      <div className="control-buttons">
        <button
          id="select"
          className={selectClickable ? '' : 'non-clickable'}
          name="select_btn"
          onClick={() => {
            if (!selectClickable) return;
            fireSelectorAction('select');
          }}
        >
          Select
        </button>
        <button
          id="cancel"
          className="default-btn"
          name="cancel_btn"
          onClick={() => {
            fireSelectorAction('cancel');
            hideRatingDialogBox();
          }}
        >
          Cancel
        </button>
      </div>

      {/* §30.2 unsaved-changes popup. */}
      <div className={`popup-wrapper${savePopupOpen ? ' active' : ''}`} id="save_changes_popup">
        <div className="popup-content">
          <h2 className="popup-heading">Save changes?</h2>
          <div className="clearfix popup-content-part">
            <p>If you choose to Close, all changes will be lost.</p>
          </div>
          <div className="popup-control-buttons">
            <button
              className="save_changes_btn not-highlighted-btn"
              name="save_changes_btn"
              data-for="#select"
              onClick={() => onSaveChanges('select')}
            >
              Save
            </button>
            <button
              className="default-btn highlighted-btn cancel_changes_btn"
              name="cancel_changes_btn"
              data-for="#cancel"
              onClick={() => onSaveChanges('cancel')}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* §30.7 notes popup (chassis-owned; special_options does not use it). */}
      {notes && (
        <NotesPopup notes={notes} open onClose={() => setNotes(null)} />
      )}
    </div>
  );
};
